import type React from 'react';
import { useRef, useImperativeHandle, useCallback } from 'react';
import type { QuestionnaireHandle, ScrollIntoViewOptionsArgs } from '../types';
const useScrollQuestions = (questionnaireRef: React.Ref<QuestionnaireHandle>) => {
  // Map of questionId -> DOM node for scrolling
  const questionIdToNodeRef = useRef(new Map<string, HTMLElement>());

  // Register a question node for scrolling
  const registerQuestionNode = useCallback((questionId: string, node: HTMLElement | null) => {
    const map = questionIdToNodeRef.current;
    if (node) {
      map.set(questionId, node);
    } else {
      map.delete(questionId);
    }
  }, []);

  // Scroll to a question
  const scrollToQuestion = useCallback((questionId: string, opts?: ScrollIntoViewOptionsArgs) => {
    const node = questionIdToNodeRef.current.get(questionId);
    if (!node) return;
    if (opts?.offsetTop != null) {
      // offsetTop helps align for sticky headers
      const top = node.getBoundingClientRect().top + window.scrollY - opts.offsetTop;
      window.scrollTo({
        top,
        behavior: opts.behavior ?? 'smooth'
      });
      return;
    }
    node.scrollIntoView({
      behavior: opts?.behavior ?? 'smooth',
      block: opts?.block ?? 'start',
      inline: opts?.inline ?? 'nearest'
    });
  }, []);
  useImperativeHandle(questionnaireRef, () => ({
    scrollToQuestion
  }), [scrollToQuestion]);
  return {
    registerQuestionNode
  };
};
export { useScrollQuestions };